import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RootState, useAppDispatch } from '../../store';
import { BaseForm, BaseFormFieldConfig, BaseFormSectionConfig } from '../shared/FormComponents';
import { createFolderAction, fetchFolderTree, FolderTreeItem, fetchTags, assignTag } from '../../store/actions/FolderAction';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { DropdownOption } from '../shared/TailwindComponents/Dropdown';
import { FolderProps, TagItem } from './documents/types';
import CollaboratorSelector, { Collaborator } from '../shared/CollaboratorSelector';
import Badge from '../shared/TailwindComponents/Badge';
import FieldShell, { FOCUS_RING } from '../shared/TailwindComponents/FieldShell';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import { FormSchema, FormValidator } from '../../utils/validation/form-schema';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, createFolderFormTourSteps } from '../shared/FeatureTour';
import { sanitizeString } from '../../utils/validation';
import Skeleton from '../shared/TailwindComponents/Skeleton';

interface CreateFolderFormProps {
  onClose: () => void;
  existingFolders: FolderProps[];
  selectedFolderItem?: any;
  onRefresh?: (createdFolderId?: string) => void | Promise<void>;
}

const CreateFolderForm: React.FC<CreateFolderFormProps> = ({
  onClose,
  existingFolders,
  selectedFolderItem,
  onRefresh
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const currentUser = useSelector((state: RootState) => (state as any).auth?.user);

  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [selectedParentFolderID, setSelectedParentFolderID] = useState<string>(selectedFolderItem?.FolderID || '');
  const [isLoadingCollaborators, setIsLoadingCollaborators] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [folderTree, setFolderTree] = useState<FolderTreeItem[]>([]);
  const [adminUuid, setAdminUuid] = useState<string | undefined>(undefined);
  const [orgTags, setOrgTags] = useState<TagItem[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'create-folder-form',
    steps: createFolderFormTourSteps,
    autoStartDelay: 600,
  });

  // Fetch folder tree and tags on mount
  useEffect(() => {
    setIsLoadingData(true);
    Promise.all([
      (dispatch(fetchFolderTree()) as any).catch(() => []),
      (dispatch(fetchTags()) as any).catch(() => []),
    ]).then(([tree, tags]) => {
      setFolderTree(tree || []);
      setOrgTags(tags || []);
      setIsLoadingData(false);
    });
  }, [dispatch]);

  // Build hierarchical dropdown options from folder tree
  const folderOptions: DropdownOption[] = useMemo(() => {
    const options = folderTree.map((f) => ({
      id: f.id,
      value: f.id,
      label: f.label,
      parent_id: f.parent_id,
    }));

    // Add the currently viewed folder if provided and not already in tree
    if (selectedFolderItem && !options.find(o => o.id === selectedFolderItem.FolderID)) {
      options.push({
        id: selectedFolderItem.FolderID,
        value: selectedFolderItem.FolderID,
        label: selectedFolderItem.FolderName,
        parent_id: null,
      });
    }

    return options;
  }, [folderTree, selectedFolderItem]);

  // Set collaborators from tree data when parent folder changes
  useEffect(() => {
    if (selectedParentFolderID) {
      const selectedFolder = folderTree.find(f => f.id === selectedParentFolderID);
      if (selectedFolder && selectedFolder.collaborators) {
        // Find admin collaborator (the one with 'admin' access)
        const admin = selectedFolder.collaborators.find(c => c.access === 'admin');
        setAdminUuid(admin?.user_uuid);

        // Filter out current user, map to collaborator format
        const filtered = selectedFolder.collaborators
          .filter((c: any) => c.user_uuid !== currentUser?.uuid)
          .map((c: any) => ({
            collaborator_uuid: c.user_uuid,
            email: c.email,
            name: c.name,
            access: c.access || 'view',
            role: c.role,
            initials: c.initials,
            avatar_url: c.avatar_url
          }));
        setCollaborators(filtered);
      } else {
        setCollaborators([]);
        setAdminUuid(undefined);
      }
    } else {
      setCollaborators([]);
      setAdminUuid(undefined);
    }
  }, [selectedParentFolderID, folderTree, currentUser?.uuid]);

  const formSections: BaseFormSectionConfig[] = useMemo(() => {
    return [
      {
        title: t('Folder details'),
        description: t('Where the folder lives and what it is called.'),
        fields: [
          {
            name: 'folderName',
            type: 'input',
            label: t('Folder Name'),
            placeholder: t('e.g., Contracts 2025'),
            required: true,
            gridSize: 'full'
          } as BaseFormFieldConfig,
          {
            name: 'parentFolder',
            type: 'dropdown',
            label: t('Parent Folder'),
            options: folderOptions,
            placeholder: t('Choose a folder'),
            // Optional by omission (no required marker); the description says
            // what leaving it empty does rather than tagging it "(optional)".
            // BaseForm's dropdown branch forwards customProps, not `description`.
            required: false,
            customProps: {
              fullWidth: true,
              hierarchical: true,
              description: t('Leave empty to create the folder at the root level.'),
            },
            gridSize: 'full',
            onChange: (value: string) => setSelectedParentFolderID(value)
          } as BaseFormFieldConfig
        ]
      },
      ...(orgTags.length > 0 ? [{
        title: t('Tags'),
        description: t('Optional labels that make this folder easier to find later.'),
        fields: [
          {
            name: 'tags',
            type: 'custom',
            label: '',
            gridSize: 'full',
            customComponent: ({ value, onChange, error }: any) => {
              const selected: number[] = value || [];
              return (
                <FieldShell error={error} translate={false}>
                  <div className="flex flex-wrap gap-1.5">
                    {orgTags.map(tag => {
                      const isSelected = selected.includes(tag.id);
                      return (
                        <button
                          key={tag.id}
                          type="button"
                          aria-pressed={isSelected}
                          onClick={() => {
                            const next = isSelected
                              ? selected.filter((id: number) => id !== tag.id)
                              : [...selected, tag.id];
                            onChange(next);
                            setSelectedTagIds(next);
                          }}
                          className={`rounded-full transition-all ${FOCUS_RING}`}
                        >
                          {/* Selected state lives on the badge - a solid
                              coloured border and a check - not as a soft ring
                              floating around the pill. */}
                          <Badge
                            color={(tag.color as BadgeColor) || 'gray'}
                            selected={isSelected}
                            className="text-xs cursor-pointer"
                          >
                            {tag.name}
                          </Badge>
                        </button>
                      );
                    })}
                  </div>
                </FieldShell>
              );
            }
          } as BaseFormFieldConfig
        ]
      }] : []),
      {
        title: t('Share with team members'),
        description: selectedParentFolderID
          ? t('People from parent folder are pre-selected. Admin access cannot be removed.')
          : t('Add or remove access to this folder'),
        fields: [
          {
            // The section title is the label - the control draws no second one.
            name: 'collaborators',
            type: 'collaboratorSelector',
            label: '',
            gridSize: 'full',
            customProps: { adminUuid }
          } as BaseFormFieldConfig
        ]
      }
    ];
  }, [folderOptions, t, selectedParentFolderID, adminUuid, orgTags]);

  const schema = useMemo(() => {
    const fields: any = {
      folderName: (value: any) => {
        if (!value || !String(value).trim()) {
          return { isValid: false, error: t('Folder name is required') };
        }
        return { isValid: true };
      },
      parentFolder: () => ({ isValid: true }),
      tags: () => ({ isValid: true }),
      collaborators: () => ({ isValid: true })
    };

    return new FormSchema(fields);
  }, [t]);

  const handleFormSubmit = async (formData: Record<string, any>) => {
    setIsSubmitting(true);
    try {
      const sanitizedFolderName = formData.folderName
        ? sanitizeString(formData.folderName, 255, false)
        : '';

      const validCollaborators = (formData.collaborators || [])
        .map((c: any) => ({
          user_id: c.user_id || c.collaborator_uuid,
          access: c.access
        }))
        .filter((c: any) => c.user_id);

      let createdFolderId: string | null = null;

      // Dispatch the create folder action and capture the response
      const result = dispatch(
        createFolderAction({
          FolderName: sanitizedFolderName,
          ParentFolderID: formData.parentFolder || undefined,
          collaborators: validCollaborators.length > 0 ? validCollaborators : undefined
        })
      ) as any;

      // If it's a Promise, await it
      if (result && typeof result.then === 'function') {
        try {
          const response = await result;
          console.log('[CreateFolderForm] Response from createFolderAction:', response);
          createdFolderId = response?.FolderID || response?.id;
        } catch (err) {
          console.error('[CreateFolderForm] Error awaiting action result:', err);
        }
      } else {
        // If not a promise, try to get the ID from the response
        createdFolderId = result?.FolderID || result?.id;
      }

      dispatch(
        showFeedbackModal({
          showModal: true,
          message: `Folder "${sanitizedFolderName}" created successfully.`,
          modalType: 'success',
          duration: 3000
        })
      );

      // Assign tags if any were selected
      if (createdFolderId && selectedTagIds.length > 0) {
        for (const tagId of selectedTagIds) {
          await dispatch(assignTag(tagId, 'add', undefined, createdFolderId) as any);
        }
      }

      // Nothing of this run is carried forward: the next open starts blank.
      setSelectedTagIds([]);
      setCollaborators([]);
      setSelectedParentFolderID('');

      // Navigate to the newly created folder. One transition - pushing the
      // folder and then calling onClose() (which pushes the path the form was
      // opened from) bounced back to where we started.
      if (createdFolderId) {
        history.push(`/files/${createdFolderId}`);
      } else {
        console.warn('[CreateFolderForm] No folder ID received, refreshing instead');
        // Fallback to refresh if no ID returned
        if (onRefresh) {
          await onRefresh();
        }
        onClose();
      }
    } catch (error: any) {
      console.error('[CreateFolderForm] Submission error:', error);
      dispatch(
        showFeedbackModal({
          showModal: true,
          message: t('Failed to create folder. Please try again.'),
          modalType: 'error',
          duration: 5000
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingData) {
    return <Skeleton variant="form" rows={6} />;
  }

  return (
    <div className="flex flex-1 flex-col min-h-0" data-tour-id="create-folder-form">
      <BaseForm
        title={t('Create New Folder')}
        description={t('Set up a new folder for your files.')}
        sections={formSections}
        schema={schema}
        initialValues={{
          folderName: '',
          parentFolder: (selectedFolderItem?.FolderID) || '',
          tags: [],
          collaborators: collaborators
        }}
        onSubmit={handleFormSubmit}
        onCancel={onClose}
        submitLabel={t('Create Folder')}
        cancelLabel={t('Cancel')}
        isSubmitting={isSubmitting}
        maxWidth="max-w-3xl"
        loadingText={t('Creating folder...')}
      />
      <FeatureTour
        steps={createFolderFormTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        disableOverlay
      />
    </div>
  );
};

export default CreateFolderForm;
